import { useState, useEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';

interface TTSProgress {
  storyId: string;
  currentPosition: number;
  totalLength: number;
  timestamp: number;
}

interface UseTextToSpeechProps {
  storyId: string;
  title: string;
  content: string;
}

interface UseTextToSpeechReturn {
  isPlaying: boolean;
  isPaused: boolean;
  isLoading: boolean;
  progress: number;
  currentText: string;
  currentSentence: string;
  currentPosition: number;
  totalLength: number;
  play: () => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setPosition: (position: number) => void;
}

const cleanTextForSpeech = (htmlText: string): string => {
  return htmlText
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
    .replace(/&amp;/g, '&') // Replace ampersand entities
    .replace(/&lt;/g, '<') // Replace less-than entities
    .replace(/&gt;/g, '>') // Replace greater-than entities
    .replace(/&quot;/g, '"') // Replace quote entities
    .replace(/&#39;/g, "'") // Replace apostrophe entities
    .replace(/&[a-zA-Z0-9#]+;/g, '') // Remove any other HTML entities
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim(); // Remove leading/trailing whitespace
};

export const useTextToSpeech = ({ 
  storyId, 
  title, 
  content 
}: UseTextToSpeechProps): UseTextToSpeechReturn => {
  const { i18n } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(0);

  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const fullTextRef = useRef<string>('');
  const positionRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number>(0);
  const estimatedDurationRef = useRef<number>(0);

  const cookieKey = `tts_progress_${storyId}`;

  const cleanedContent = cleanTextForSpeech(content);
  const fullText = `${title}. ${cleanedContent}`;

  const saveProgress = useCallback((position: number) => {
    const progressData: TTSProgress = {
      storyId,
      currentPosition: position,
      totalLength: fullText.length,
      timestamp: Date.now()
    };
    Cookies.set(cookieKey, JSON.stringify(progressData), { expires: 30 });
  }, [storyId, fullText.length, cookieKey]);

  const loadProgress = useCallback((): number => {
    try {
      const saved = Cookies.get(cookieKey);
      if (saved) {
        const progressData: TTSProgress = JSON.parse(saved);
        const isRecent = Date.now() - progressData.timestamp < 24 * 60 * 60 * 1000;
        
        if (progressData.storyId === storyId && isRecent) {
          return progressData.currentPosition;
        }
      }
    } catch (error) {
      console.warn('Failed to load TTS progress:', error);
    }
    return 0;
  }, [storyId, cookieKey]);

  const estimateDuration = useCallback((text: string, rate: number = 0.9): number => {
    const wordsPerMinute = 150 * rate;
    const wordCount = text.split(/\s+/).length;
    return (wordCount / wordsPerMinute) * 60 * 1000;
  }, []);

  const selectBestVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    const currentLang = i18n.language;
    

    // Language mapping for voice selection
    const languageMap: Record<string, string[]> = {
      'bg': ['bg-BG', 'bg'],
      'en': ['en-US', 'en-GB', 'en']
    };

    const targetLangs = languageMap[currentLang] || languageMap[currentLang.split('-')[0]] || ['en-US', 'en'];
    
    // Priority order: Local voices first, then remote voices
    for (const targetLang of targetLangs) {
      // First try to find local voices (better performance)
      const localVoice = voices.find(voice => 
        voice.lang.startsWith(targetLang) && voice.localService
      );
      
      if (localVoice) {
        return localVoice;
      }

      // Then try Google voices or other high-quality voices
      const googleVoice = voices.find(voice => 
        voice.lang.startsWith(targetLang) && 
        (voice.name.includes('Google') || voice.name.includes('Microsoft'))
      );
      
      if (googleVoice) {
        return googleVoice;
      }

      // Finally, any voice for the target language
      const anyVoice = voices.find(voice => voice.lang.startsWith(targetLang));
      
      if (anyVoice) {
        return anyVoice;
      }
    }

    // Ultimate fallback to any English voice
    const fallbackVoice = voices.find(voice => voice.lang.startsWith('en'));
    return fallbackVoice || null;
  }, [i18n.language]);

  const updateProgress = useCallback(() => {
    if (utteranceRef.current && fullTextRef.current && startTimeRef.current) {
      const now = Date.now();
      const elapsed = now - startTimeRef.current;
      const estimatedProgress = Math.min((elapsed / estimatedDurationRef.current) * 100, 100);
      
      setProgress(estimatedProgress);
      
      const estimatedPosition = Math.floor((estimatedProgress / 100) * fullTextRef.current.length);
      positionRef.current = Math.max(positionRef.current, estimatedPosition);
      setCurrentPosition(positionRef.current);
      saveProgress(positionRef.current);
    }
  }, [saveProgress]);

  const startProgressTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    
    intervalRef.current = setInterval(() => {
      updateProgress();
    }, 500);
  }, [updateProgress]);

  const stopProgressTracking = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const speak = useCallback((text: string, startPosition: number = 0) => {
    if (!('speechSynthesis' in window)) {
      alert('Text-to-speech is not supported in your browser');
      return;
    }

    window.speechSynthesis.cancel();
    setIsLoading(true);

    const initSpeech = () => {
      const textToSpeak = startPosition > 0 ? text.substring(startPosition) : text;
      const utterance = new SpeechSynthesisUtterance(textToSpeak);
      
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;

      const bestVoice = selectBestVoice();
      if (bestVoice) {
        utterance.voice = bestVoice;
      }

    utterance.onstart = () => {
      setIsLoading(false);
      setIsPlaying(true);
      setIsPaused(false);
      fullTextRef.current = text;
      positionRef.current = startPosition;
      startTimeRef.current = Date.now();
      estimatedDurationRef.current = estimateDuration(textToSpeak);
      
      const initialProgress = (startPosition / text.length) * 100;
      setProgress(initialProgress);
      
      startProgressTracking();
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setIsPaused(false);
      stopProgressTracking();
      setProgress(100);
      positionRef.current = fullText.length;
      
      setTimeout(() => {
        saveProgress(0);
      }, 1000);
    };

    utterance.onerror = () => {
      setIsLoading(false);
      setIsPlaying(false);
      setIsPaused(false);
      stopProgressTracking();
    };

    utterance.onpause = () => {
      setIsPaused(true);
      stopProgressTracking();
    };

      utterance.onresume = () => {
        setIsPaused(false);
        startTimeRef.current = Date.now() - ((progress / 100) * estimatedDurationRef.current);
        startProgressTracking();
      };

      utteranceRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    };

    // Ensure voices are loaded before speaking
    const voices = window.speechSynthesis.getVoices();
    if (voices.length === 0) {
      // Voices not loaded yet, wait for them
      window.speechSynthesis.onvoiceschanged = () => {
        initSpeech();
        window.speechSynthesis.onvoiceschanged = null; // Clear the event listener
      };
    } else {
      // Voices already loaded
      initSpeech();
    }
  }, [startProgressTracking, stopProgressTracking, saveProgress, fullText.length, estimateDuration, progress, selectBestVoice]);

  const play = useCallback(() => {
    const savedPosition = loadProgress();
    
    if (savedPosition >= fullText.length - 10) {
      setCurrentPosition(0);
      setProgress(0);
      positionRef.current = 0;
      speak(fullText, 0);
    } else {
      setCurrentPosition(savedPosition);
      speak(fullText, savedPosition);
    }
  }, [fullText, loadProgress, speak]);

  const pause = useCallback(() => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, []);

  const resume = useCallback(() => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, []);

  const stop = useCallback(() => {
    window.speechSynthesis.cancel();
    setIsPlaying(false);
    setIsPaused(false);
    setProgress(0);
    stopProgressTracking();
    positionRef.current = 0;
    saveProgress(0);
  }, [stopProgressTracking, saveProgress]);

  const setPosition = useCallback((position: number) => {
    const clampedPosition = Math.max(0, Math.min(position, fullText.length));
    setCurrentPosition(clampedPosition);
    positionRef.current = clampedPosition;
    
    const newProgress = (clampedPosition / fullText.length) * 100;
    setProgress(newProgress);
    
    if (isPlaying) {
      window.speechSynthesis.cancel();
      speak(fullText, clampedPosition);
    }
    
    saveProgress(clampedPosition);
  }, [fullText, isPlaying, speak, saveProgress]);

  const getCurrentText = useCallback(() => {
    if (currentPosition === 0) return fullText;
    return fullText.substring(currentPosition);
  }, [fullText, currentPosition]);

  const getCurrentSentence = useCallback(() => {
    if (!fullText || currentPosition === 0) return '';
    
    const textFromPosition = fullText.substring(currentPosition);
    const sentences = textFromPosition.split(/[.!?]+/);
    return sentences[0]?.trim() || '';
  }, [fullText, currentPosition]);

  useEffect(() => {
    fullTextRef.current = fullText;
    const savedPosition = loadProgress();
    setCurrentPosition(savedPosition);
    setProgress((savedPosition / fullText.length) * 100);
  }, [fullText, loadProgress]);

  // Preload voices on mount
  useEffect(() => {
    if ('speechSynthesis' in window) {
      // Trigger voice loading by calling getVoices
      window.speechSynthesis.getVoices();
      
      // Set up voice loading listener if needed
      if (window.speechSynthesis.getVoices().length === 0) {
        const handleVoicesChanged = () => {
          window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
        };
        window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
      stopProgressTracking();
    };
  }, [stopProgressTracking]);

  return {
    isPlaying,
    isPaused,
    isLoading,
    progress,
    currentText: getCurrentText(),
    currentSentence: getCurrentSentence(),
    currentPosition,
    totalLength: fullText.length,
    play,
    pause,
    resume,
    stop,
    setPosition,
  };
};